import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Plus, Building2, MapPin, Phone, Edit } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', address: '', phone: '' });

  const fetchBranches = async () => {
    try { const { data } = await api.get('/branches'); setBranches(data); } catch {}
  };
  useEffect(() => { fetchBranches(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editId) {
      await api.put(`/branches/${editId}`, form);
    } else {
      await api.post('/branches', form);
    }
    setShowModal(false);
    setEditId(null);
    setForm({ name: '', code: '', address: '', phone: '' });
    fetchBranches();
  };

  const openEdit = (b) => {
    setEditId(b._id);
    setForm({ name: b.name, code: b.code, address: b.address, phone: b.phone });
    setShowModal(true);
  };

  const toggleActive = async (b) => {
    if (b.is_active) {
      await api.put(`/branches/${b._id}/deactivate`);
    } else {
      await api.put(`/branches/${b._id}/activate`);
    }
    fetchBranches();
  };

  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Branches</h1>
        <Button onClick={() => { setEditId(null); setForm({ name: '', code: '', address: '', phone: '' }); setShowModal(true); }} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="add-branch-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Branch
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="branch-cards">
        {branches.map(b => (
          <Card key={b._id} className="bg-white border-[#E5F2F1] rounded-2xl hover:shadow-[0_8px_30px_rgba(13,139,125,0.08)] transition-all duration-300">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0D8B7D]/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-[#0D8B7D]" /></div>
                  <div>
                    <p className="text-sm font-semibold text-[#1A3330]">{b.name}</p>
                    <p className="text-[10px] text-[#4A6B68] uppercase tracking-wider">{b.code}</p>
                  </div>
                </div>
                <Badge className={`text-[10px] border-0 ${b.is_active ? 'bg-[#2E8B57]/10 text-[#2E8B57]' : 'bg-[#D3544E]/10 text-[#D3544E]'}`}>{b.is_active ? 'Active' : 'Inactive'}</Badge>
              </div>
              <div className="space-y-1.5 text-xs text-[#4A6B68] mb-3">
                <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{b.address}</p>
                <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{b.phone}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => openEdit(b)} className="text-[#0D8B7D] text-xs h-7" data-testid={`edit-branch-${b._id}`}><Edit className="w-3 h-3 mr-1" />Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => toggleActive(b)} className={`text-xs h-7 ${b.is_active ? 'text-[#D3544E]' : 'text-[#2E8B57]'}`} data-testid={`toggle-branch-${b._id}`}>{b.is_active ? 'Deactivate' : 'Activate'}</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="branch-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">{editId ? 'Edit Branch' : 'Add Branch'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className="text-sm">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="branch-name" /></div>
            <div><Label className="text-sm">Code</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} required placeholder="POR, VAD, OMR..." className="rounded-xl border-[#E5F2F1]" data-testid="branch-code" /></div>
            <div><Label className="text-sm">Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="branch-address" /></div>
            <div><Label className="text-sm">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="branch-phone" /></div>
            <Button type="submit" className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-branch-btn">{editId ? 'Update' : 'Create'} Branch</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
